import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, Navigate } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import VoiceCard from "@/components/VoiceCard";
import ShortlistButton from "@/components/ShortlistButton";
import Waveform from "@/components/Waveform";
import { voicePath, slugify } from "@/config/childVoices";
import { usePlayer } from "@/context/PlayerContext";
import { Play, Pause, Loader2, ArrowRight, ChevronRight, Sparkles } from "lucide-react";

export default function TalentProfile() {
  const { lang, name } = useParams();
  const navigate = useNavigate();
  const player = usePlayer();
  const [t, setT] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/voices/${lang}/${name}`).then((r) => {
      setT(r.data);
      if (window.gtag) window.gtag("event", "voice_profile_viewed");
      api.get("/voices", { params: { language: r.data.language } }).then((rr) =>
        setRelated(rr.data.filter((x) => x.id !== r.data.id).slice(0, 3)));
    }).catch(() => setT(null)).finally(() => setLoading(false));
  }, [lang, name]);

  if (loading) return <div className="flex justify-center py-40 text-brand-violet"><Loader2 className="animate-spin" size={32} /></div>;
  if (!t) return <div className="container-x py-40 text-center"><h1 className="font-heading text-3xl font-bold">Voice not found</h1><Link to="/child-voices" className="mt-4 inline-block text-brand-violet">Back to voices</Link></div>;

  const ageLabel = t.age_min === t.age_max ? `Age ${t.age_min}` : `Age ${t.age_min}–${t.age_max}`;

  const base = "https://www.voiceoverkids.agency";
  const canonical = base + voicePath(t);
  const personLd = {
    "@context": "https://schema.org", "@type": "Person",
    name: t.name, url: canonical, jobTitle: "Child Voice Actor", knowsLanguage: t.language,
    ...(t.photo_url ? { image: mediaUrl(t.photo_url) } : {}),
    ...(t.description ? { description: t.description } : {}),
  };
  const audioLd = (t.demos || []).map((d) => ({
    "@context": "https://schema.org", "@type": "AudioObject",
    name: `${t.name} — ${d.title}`, contentUrl: mediaUrl(d.url), encodingFormat: "audio/mpeg",
    inLanguage: t.language, ...(d.category ? { description: d.category } : {}),
    creator: { "@type": "Person", name: t.name },
  }));
  const jsonLd = [personLd, ...audioLd];

  return (
    <>
      <SEO title={`${t.name} — ${t.language} Child Voice`} path={voicePath(t)} jsonLd={jsonLd}
        description={`Listen to ${t.name}, a ${t.language} child voice actor (${ageLabel}). Add to shortlist or request this voice.`} />
      <div className="container-x pt-6 text-sm text-zinc-500 flex flex-wrap items-center gap-1">
        <Link to="/child-voices" className="hover:text-brand-violet">Voices</Link><ChevronRight size={14} />
        <Link to={`/child-voices/${slugify(t.language)}`} className="hover:text-brand-violet">{t.language}</Link><ChevronRight size={14} />
        <span className="text-zinc-800">{t.name}</span>
      </div>

      <section className="container-x grid gap-10 py-10 lg:grid-cols-[340px_1fr]">
        <div>
          <div className="overflow-hidden rounded-3xl border border-zinc-200 bg-zinc-100">
            {t.photo_url ? <img src={mediaUrl(t.photo_url)} alt={t.name} className="aspect-square w-full object-cover" />
              : <div className="aspect-square" />}
          </div>
        </div>
        <div>
          {t.voice_type === "ai" && <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-3 py-1 text-xs font-bold text-brand-violet mb-3"><Sparkles size={12} /> AI Voice</span>}
          <h1 className="font-heading text-4xl font-black tracking-tight md:text-5xl">{t.name}</h1>
          <div className="mt-3 flex flex-wrap gap-2 text-sm">
            {[t.language, t.country, ageLabel, t.gender === "female" ? "Girl" : "Boy", t.accent].filter(Boolean).map((x) => (
              <span key={x} className="rounded-full bg-zinc-100 px-3 py-1 font-medium text-zinc-700">{x}</span>
            ))}
          </div>
          {t.description && <p className="mt-5 max-w-xl text-zinc-600">{t.description}</p>}

          <div className="mt-6 flex flex-wrap gap-3">
            <ShortlistButton talent={t} withLabel />
            <Link to={`/casting?voice=${encodeURIComponent(t.name)}`} data-testid="request-voice"
              className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-6 py-2.5 font-bold text-white transition hover:bg-brand-violetHover">
              Request this voice <ArrowRight size={16} />
            </Link>
          </div>

          <div className="mt-8 space-y-3">
            <h2 className="font-heading text-lg font-bold">Audio demos</h2>
            {(t.demos || []).map((d) => {
              const key = `${t.id}:${d.id}`;
              const active = player.isActive(key);
              const isPlaying = active && player.playing;
              return (
                <div key={d.id} className="flex items-center gap-4 rounded-2xl border border-zinc-200 bg-white p-4">
                  <button data-testid={`demo-play-${d.id}`}
                    onClick={() => player.playTrack({ key, name: t.name, sub: `${d.title}`, url: mediaUrl(d.url), photo: mediaUrl(t.photo_url), talentId: t.id })}
                    className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-violet text-white transition hover:bg-brand-violetHover">
                    {active && player.loading ? <Loader2 size={18} className="animate-spin" /> : isPlaying ? <Pause size={18} className="fill-white" /> : <Play size={18} className="fill-white ml-0.5" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <span className="truncate font-semibold text-zinc-800">{d.title}</span>
                      <span className="shrink-0 rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-500">{d.category}</span>
                    </div>
                    <Waveform seed={d.id} count={56} height={28} className="mt-2"
                      active={active} progress={active && player.duration ? player.progress / player.duration : 0}
                      onSeek={active ? player.seek : undefined} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-white section-y">
          <div className="container-x">
            <div className="mb-8 flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold">Cast similar voices</h2>
              <button onClick={() => navigate(`/child-voices/${(t.language||'').toLowerCase().replace(/[^a-z0-9]+/g,'-')}`)} className="text-sm font-semibold text-brand-violet">See all {t.language}</button>
            </div>
            <div className="grid gap-6 md:grid-cols-3">{related.map((r) => <VoiceCard key={r.id} talent={r} />)}</div>
          </div>
        </section>
      )}
    </>
  );
}


// Legacy /voices/<uuid> -> clean /voices/<language>/<name> (preserves indexed links)
export function VoiceLegacyRedirect() {
  const { id } = useParams();
  const [dest, setDest] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    api.get(`/voices/${id}`).then((r) => setDest(voicePath(r.data))).catch(() => setNotFound(true));
  }, [id]);
  if (notFound) return (
    <div className="container-x py-40 text-center">
      <h1 className="font-heading text-3xl font-bold">Voice not found</h1>
      <Link to="/child-voices" className="mt-4 inline-block text-brand-violet">Back to voices</Link>
    </div>
  );
  if (!dest) return <div className="flex justify-center py-40 text-brand-violet"><Loader2 className="animate-spin" size={32} /></div>;
  return <Navigate to={dest} replace />;
}
